import { zodResolver } from '@hookform/resolvers/zod';
import type { UpdateIntegrationConfigurationResponse } from '@weft/api-definition';
import { NetSuiteIntegrationConfigurationSchema, type NetSuiteIntegrationConfiguration } from '@weft/types';
import { isNotNullOrUndefined } from '@weft/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { HandleUpdateConfiguration } from '../../configuration-tab-content-types';
import { SyncConfigurationSection } from './net-suite-configuration-components';
import { AccountConfigurationSection } from './net-suite-configuration-components/AccountConfigurationSection';
import { ProductsConfigurationSection } from './net-suite-configuration-components/ProductsConfigurationSection';
import { NET_SUITE_CONFIGURATION_SECTIONS } from './net-suite-configuration-consts/net-suite-configuration-sections';
import type { NetSuiteIntegrationConfigurationWithSections } from './net-suite-configuration-types/net-suite-configuration-type';

export type NetSuiteConfigurationProps = {
  configuration: NetSuiteIntegrationConfiguration;
  handleSubmit: HandleUpdateConfiguration;
};

export const NetSuiteConfiguration: React.FC<NetSuiteConfigurationProps> = ({ configuration, handleSubmit }) => {
  const methods = useForm<NetSuiteIntegrationConfigurationWithSections>({
    resolver: zodResolver(NetSuiteIntegrationConfigurationSchema),
    defaultValues: { ...configuration, sectionToEdit: NET_SUITE_CONFIGURATION_SECTIONS.none },
  });
  
  const handleUpdateConfigurationSuccess = ({ integrationConfiguration }: UpdateIntegrationConfigurationResponse) => {
    methods.reset({ ...integrationConfiguration });
  };

  const onSubmit = methods.handleSubmit(async (data) => {
    const validatedConfigurationInput = getValidConfigurationInput(data);
    if (isNotNullOrUndefined(validatedConfigurationInput)) {
      handleSubmit(validatedConfigurationInput, handleUpdateConfigurationSuccess);
    }

    methods.setValue('sectionToEdit', NET_SUITE_CONFIGURATION_SECTIONS.none);
  });

  return (
    <FormProvider
      {...methods}
    >
      <form
        onSubmit={onSubmit}
        className='relative flex h-fit min-h-fit flex-col gap-5 p-16'
      >
        <AccountConfigurationSection />
        <ProductsConfigurationSection />
        <SyncConfigurationSection />
      </form>
    </FormProvider>
  );
};

function getValidConfigurationInput(configurationInput: NetSuiteIntegrationConfiguration | null) {
  const configurationInputParseResult = NetSuiteIntegrationConfigurationSchema.safeParse(configurationInput);

  return configurationInputParseResult.success ? configurationInputParseResult.data : null;
}